import { useQuery } from "@tanstack/react-query";

import { isApiClientError } from "../api/errors";
import type { DocumentWorkflowFileVersion } from "../entities/document-workflow";
import { useDocumentFiles } from "./documentFilesContext";
import { ScanStatusBadge, SignatureStateBadge } from "./FileBadges";
import { displayDateTime } from "./fileLabels";
import { FileProvenance } from "./FileProvenance";
import { fileTypeLabel, formatBytes } from "./formatting";
import { VersionControls } from "./VersionControls";
import { Notice } from "./Notice";

function historyError(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 403) {
      return "История версий недоступна: нет права на файлы документа.";
    }
    if (error.status === 404) return "Файл не найден или больше не доступен.";
  }

  return "Не удалось загрузить историю версий. Повторите попытку.";
}

function VersionEntry({
  version,
  displayName,
  isCurrent,
}: {
  version: DocumentWorkflowFileVersion;
  displayName: string;
  isCurrent: boolean;
}) {
  const { authorName } = useDocumentFiles();

  return (
    <li
      className="version-entry"
      aria-label={`Версия ${version.version_number}`}
    >
      <div className="version-entry__head">
        <strong>Версия {version.version_number}</strong>
        {isCurrent ? (
          <span className="version-entry__current">Актуальная</span>
        ) : null}
        <ScanStatusBadge status={version.scan_status} />
        <SignatureStateBadge state={version.signature_state} />
      </div>
      <dl className="version-entry__facts">
        <div>
          <dt>Автор</dt>
          <dd>{authorName(version.created_by)}</dd>
        </div>
        <div>
          <dt>Дата</dt>
          <dd>{displayDateTime(version.created_at)}</dd>
        </div>
        <div>
          <dt>Размер</dt>
          <dd>
            {fileTypeLabel(version.detected_mime)} ·{" "}
            {formatBytes(version.size ?? 0)}
          </dd>
        </div>
        <div>
          <dt>SHA-256</dt>
          <dd>
            {version.sha256 ? (
              <code>{version.sha256}</code>
            ) : (
              <span className="dw-secondary">не вычислен</span>
            )}
          </dd>
        </div>
      </dl>
      {version.comment ? (
        <p className="version-entry__comment">{version.comment}</p>
      ) : null}
      <FileProvenance
        provenance={version.provenance}
        referenceCount={version.reference_count}
        authorName={authorName}
      />
      <VersionControls
        version={version}
        displayName={displayName}
        withVersionInLabels
      />
    </li>
  );
}

/**
 * История версий логического файла: от новой к старой.
 *
 * Каждая версия неизменяема: здесь версии только показываются, а изменения
 * выпускаются новой версией.
 */
export function FileVersionHistory({
  fileId,
  displayName,
  currentVersionId,
}: {
  fileId: string;
  displayName: string;
  currentVersionId: string | null;
}) {
  const { services } = useDocumentFiles();
  const versions = useQuery({
    queryKey: ["document-workflow", "files", fileId, "versions"],
    queryFn: () => services.documentWorkflow.fileVersions(fileId),
    retry: false,
  });

  return (
    <section
      className="version-history"
      aria-label={`История версий: ${displayName}`}
    >
      {versions.isPending ? (
        <p aria-busy="true">Загружаем историю версий…</p>
      ) : null}
      {versions.isError ? (
        <Notice>
          <p>{historyError(versions.error)}</p>
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={() => void versions.refetch()}
          >
            Повторить загрузку
          </button>
        </Notice>
      ) : null}
      {versions.data?.length === 0 ? (
        <p className="dw-secondary">У файла нет версий.</p>
      ) : null}
      {versions.data?.length ? (
        <ol className="version-list">
          {versions.data.map((version) => (
            <VersionEntry
              key={version.id}
              version={version}
              displayName={displayName}
              isCurrent={version.id === currentVersionId}
            />
          ))}
        </ol>
      ) : null}
    </section>
  );
}
